import { Building2, ChevronLeft, ChevronRight, Loader2, UserRound, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SearchInput } from '@/components/ui/search-input'
import { SegmentedTabs } from '@/components/ui/segmented-tabs'
import { Skeleton } from '@/components/ui/skeleton'
import type { RegisterCountry, SearchKind, SnapshotSearchResponse } from '@/services/api/registers'
import { type RegisterPick, SnapshotSearchResults } from './SnapshotSearchResults'

/** What the analyst is searching for, all of it owned by the caller. */
export interface RegisterSearchState {
  country: RegisterCountry
  kind: SearchKind
  query: string
  /** 1-based. */
  page: number
}

/** The outcome of that search, as the caller's data layer sees it. */
export interface RegisterSearchResult {
  data?: SnapshotSearchResponse
  loading: boolean
  error?: string | null
  /** This country's snapshot is not loaded in this deployment. */
  unavailable?: boolean
}

/** Which record the analyst chose, and how much of its network to bring. */
export interface RegisterSelection {
  picked: RegisterPick | null
  onPick: (pick: RegisterPick | null) => void
  depth: 1 | 2
  onDepthChange: (depth: 1 | 2) => void
}

/** The import the confirm button triggers. */
export interface RegisterImportState {
  onConfirm: () => void
  pending: boolean
  error?: string | null
}

export interface RegisterPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The case entity the lookup started from — the pick is asserted to BE this. */
  subject: { name: string; typeLabel: string }
  search: RegisterSearchState
  onSearchChange: (patch: Partial<RegisterSearchState>) => void
  result: RegisterSearchResult
  selection: RegisterSelection
  importState: RegisterImportState
  /** Hits per kind per page, matching what the caller asked the API for. */
  pageSize?: number
  /** Below this many characters the API refuses the search (422). */
  minChars?: number
}

/**
 * Pick the register record that matches an entity already on the canvas.
 *
 * Presentational only — every piece of state is the caller's, so the dialog can
 * be driven from a story without a query client. The pick is deliberately a
 * two-step gesture (select a row, then confirm): it asserts an identity, and
 * the confirm bar is where the analyst sees what that assertion will pull in.
 */
export function RegisterPickerDialog({
  open,
  onOpenChange,
  subject,
  search,
  onSearchChange,
  result,
  selection: { picked, onPick, depth, onDepthChange },
  importState: { onConfirm, pending: importing, error: importError },
  pageSize = 8,
  minChars = 3,
}: RegisterPickerDialogProps) {
  const { t } = useTranslation()
  const ready = search.query.trim().length >= minChars
  const found = result.data ? Math.max(result.data.found.companies, result.data.found.persons) : 0
  const lastPage = Math.max(1, Math.ceil(found / pageSize))

  // Any change of what is being searched invalidates both the page and the
  // pick — a row index from the previous result set means nothing here.
  const change = (patch: Partial<RegisterSearchState>) => {
    onPick(null)
    onSearchChange({ page: 1, ...patch })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Phone: the dialog takes the full height the guard allows, so the
          result list between the pinned header and footer gets every pixel. */}
      <DialogContent className="flex max-h-[85vh] w-full flex-col gap-0 p-0 max-md:h-[calc(100dvh-2rem)] max-md:max-h-[calc(100dvh-2rem)] sm:max-w-3xl">
        <DialogHeader className="shrink-0 gap-2 border-b border-border p-3 md:gap-3 md:p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-base">{t('extensions.osint.picker.title')}</DialogTitle>
              <DialogDescription className="mt-1 max-md:text-xs">
                {t('extensions.osint.picker.subtitle', {
                  name: subject.name,
                  type: subject.typeLabel,
                })}
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={t('common.close')}
                className="max-md:size-8"
              >
                <X className="size-4" />
              </Button>
            </DialogClose>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SegmentedTabs<RegisterCountry>
              size="sm"
              active={search.country}
              onChange={country => change({ country })}
              tabs={[
                { value: 'cz', label: t('extensions.osint.countries.cz') },
                { value: 'sk', label: t('extensions.osint.countries.sk') },
              ]}
            />
            <SegmentedTabs<SearchKind>
              size="sm"
              active={search.kind}
              onChange={kind => change({ kind })}
              tabs={[
                { value: 'all', label: t('extensions.osint.sections.search.kind.all') },
                { value: 'company', label: t('extensions.osint.sections.search.kind.company') },
                { value: 'person', label: t('extensions.osint.sections.search.kind.person') },
              ]}
            />
          </div>

          <SearchInput
            value={search.query}
            onValueChange={value => change({ query: value })}
            placeholder={t(`extensions.osint.sections.search.placeholder.${search.country}`)}
            loading={result.loading}
            autoComplete="off"
            // The analyst opened this to look something up; the field is the
            // only thing they need.
            autoFocus
          />
          {result.unavailable && (
            <p className="text-xs text-warning-foreground">
              {t('extensions.osint.sections.search.unavailable')}
            </p>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-3 md:p-4">
          {!ready && (
            <p className="text-xs text-muted-foreground">
              {t('extensions.osint.sections.search.typeMore', { n: minChars })}
            </p>
          )}
          {ready && result.error && <p className="text-xs text-destructive">{result.error}</p>}
          {ready && !result.error && result.loading && !result.data && (
            <div className="space-y-3">
              <Skeleton className="h-24 rounded-lg" />
              <Skeleton className="h-24 rounded-lg" />
            </div>
          )}
          {ready && !result.error && result.data && (
            <SnapshotSearchResults
              stacked
              data={result.data}
              onPick={onPick}
              pickedId={picked?.id ?? null}
            />
          )}
        </div>

        {ready && result.data && found > pageSize && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground">
            <span>
              {t('extensions.osint.sections.search.pageInfo', {
                page: search.page,
                pages: lastPage,
                size: pageSize,
              })}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={search.page <= 1 || result.loading}
                onClick={() => onSearchChange({ page: Math.max(1, search.page - 1) })}
              >
                <ChevronLeft className="size-4" />
                {t('extensions.osint.sections.search.prev')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={search.page >= lastPage || result.loading}
                onClick={() => onSearchChange({ page: Math.min(lastPage, search.page + 1) })}
              >
                {t('extensions.osint.sections.search.next')}
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}

        {/* DialogFooter bleeds `-mx-4 -mb-4` to meet the rounded corners of a
            `p-4` dialog; this one is `p-0` and scrolls its own body, so the
            bleed is cancelled and the footer sits flush. */}
        <DialogFooter className="mx-0 mb-0 shrink-0 flex-col items-stretch gap-2 p-3 sm:flex-row sm:items-center sm:justify-between md:gap-3 md:p-4">
          <div className="flex min-w-0 flex-col gap-2">
            {picked ? (
              <div className="flex min-w-0 items-center gap-2 text-xs">
                {picked.kind === 'person' ? (
                  <UserRound className="size-3.5 shrink-0 text-primary" />
                ) : (
                  <Building2 className="size-3.5 shrink-0 text-primary" />
                )}
                <span className="truncate font-medium text-foreground">{picked.label}</span>
                <Badge variant="muted" className="font-mono text-2xs">
                  {picked.id}
                </Badge>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('extensions.osint.picker.pickHint')}
              </p>
            )}
            <div className="flex items-center gap-2">
              <span className="text-2xs uppercase tracking-[0.12em] text-muted-foreground">
                {t('extensions.osint.picker.depthLabel')}
              </span>
              <SegmentedTabs<'1' | '2'>
                size="sm"
                active={String(depth) as '1' | '2'}
                onChange={value => onDepthChange(value === '2' ? 2 : 1)}
                tabs={[
                  { value: '1', label: t('extensions.osint.picker.depth1') },
                  { value: '2', label: t('extensions.osint.picker.depth2') },
                ]}
              />
            </div>
            {importError && <p className="text-xs text-destructive">{importError}</p>}
          </div>
          <Button onClick={onConfirm} disabled={!picked || importing} className="shrink-0">
            {importing && <Loader2 className="size-4 animate-spin" />}
            {t('extensions.osint.picker.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
